import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { Node } from '@khc/contracts';
import { ApiError } from '../data/client';
import { createHub, deleteHub, fetchHubState } from '../data/hubs';
import { fetchAgentReleases, generateAgentCommand, type AgentCommandResult } from '../data/agent';
import {
  createCfDnsRecordForNode,
  deleteCfDnsRecordsForNode,
  fetchCfDnsRecords,
  fetchCfDnsZones,
} from '../data/cfDns';
import { classifyCfDnsRecords } from './cfDnsRecords';
import { hubsQueryOptions } from '../data/queries';
import { action, type ActionRef } from '../registry/actions';
import { ConfirmAction } from '../patterns';
import { paths } from '../registry/routes';
import {
  Banner,
  Button,
  Checkbox,
  CodeBlock,
  Drawer,
  Field,
  Grid,
  InlineError,
  KeyValue,
  PageHeader,
  Row,
  SecretField,
  Select,
  Skeleton,
  Stack,
  Table,
  TextInput,
  downloadTextFile,
  useToast,
  type ColumnDef,
} from '../ui';
import { zh, fmt } from '../i18n/zh';

const EMPTY_FORM = {
  name: '',
  endpoint: '',
  tunnelEndpoint: '',
  zoneId: '',
  subdomain: '',
  recordId: '',
  port: '8880',
};

/** 已有记录的 IP → 接入地址（IPv6 自动加方括号）。 */
function endpointFromRecord(content: string, port: string): string {
  const host = content.includes(':') && !content.startsWith('[') ? `[${content}]` : content;
  return `http://${host}:${port}`;
}

export default function HubsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const search = useSearch({ strict: false }) as { create?: boolean };
  const hubsQuery = useQuery(hubsQueryOptions());
  const hubs = hubsQuery.data ?? [];
  const [refreshing, setRefreshing] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  /** 删除节点：L3 确认 + 可选「先生成卸载 Agent 脚本」；删除后脚本仍留在抽屉里。 */
  const [deleteTarget, setDeleteTarget] = useState<Node | null>(null);
  const [deleteWithScript, setDeleteWithScript] = useState(true);
  const [deleteDns, setDeleteDns] = useState(true);
  const [uninstallScript, setUninstallScript] = useState<{
    name: string;
    command: AgentCommandResult;
  } | null>(null);

  /**
   * 手动刷新：用户显式点击后才拉列表并逐个探测 /state。
   * 打开列表页不探测任何节点（列表 status 用 D1 里的租约，unknown ≠ 离线）。
   */
  const refreshStatuses = async () => {
    setRefreshing(true);
    setProbeError(null);
    try {
      const nodes = (await hubsQuery.refetch()).data ?? [];
      const results = await Promise.allSettled(nodes.map((node) => fetchHubState(node.id)));
      const now = Math.floor(Date.now() / 1000);
      const updated = new Map<string, boolean>();
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') updated.set(nodes[index]!.id, result.value.reachable);
      });
      if (updated.size > 0) {
        queryClient.setQueryData<Node[] | undefined>(['hubs'], (old) =>
          old?.map((node) => {
            const reachable = updated.get(node.id);
            if (reachable === undefined) return node;
            return {
              ...node,
              status: reachable ? 'online' : 'offline',
              ...(reachable ? { lastSeenAt: now } : {}),
            };
          }),
        );
        for (const [id, reachable] of updated) {
          queryClient.setQueryData<Node | undefined>(['hubs', id], (old) =>
            old
              ? {
                  ...old,
                  status: reachable ? 'online' : 'offline',
                  ...(reachable ? { lastSeenAt: now } : {}),
                }
              : old,
          );
        }
      }
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) {
        setProbeError(fmt(zh.hubs.statusRefreshPartial, { count: String(failed) }));
      }
    } catch (error) {
      setProbeError(error instanceof Error ? error.message : zh.hubs.statusRefreshFailed);
    } finally {
      setRefreshing(false);
    }
  };

  const [searchText, setSearchText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  // CF DNS Zone 只在创建抽屉打开时才拉取，避免打开列表页产生额外请求。
  const cfZonesQuery = useQuery({
    queryKey: ['cfDns', 'zones'],
    queryFn: fetchCfDnsZones,
    retry: false,
    enabled: drawerOpen,
  });
  const cfZones = cfZonesQuery.data ?? [];
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState('');
  const [createdEndpoint, setCreatedEndpoint] = useState('');
  const [createdNodeId, setCreatedNodeId] = useState<string | null>(null);
  const [createdDomain, setCreatedDomain] = useState<string | null>(null);
  const [dnsError, setDnsError] = useState<string | null>(null);
  const [installCommand, setInstallCommand] = useState<string | null>(null);
  const [installLoading, setInstallLoading] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  /** P0-1：安装命令可选组件（默认全不勾，避免把不需要的组件装进机器）。 */
  const [installOpts, setInstallOpts] = useState({ withCaddy: false, tls: false, tlsCn: '' });

  // 读取所选 Zone 的已有记录（只读）；失败时保留手填子域名兜底。
  const recordsZoneId = drawerOpen && !token ? form.zoneId : '';
  const cfRecordsQuery = useQuery({
    queryKey: ['cfDns', 'records', recordsZoneId],
    queryFn: () => fetchCfDnsRecords(recordsZoneId),
    enabled: Boolean(recordsZoneId),
    retry: false,
  });
  const recordCandidates = classifyCfDnsRecords(
    cfRecordsQuery.data?.records ?? [],
    hubsQuery.data ?? [],
  );
  const selectedCandidate = recordCandidates.find((item) => item.record.id === form.recordId);
  const selectedRecord = selectedCandidate?.record ?? null;
  const recordOptions = recordCandidates.map((candidate) => {
    const reason =
      candidate.conflict === 'multi'
        ? zh.hubs.cfDnsRecordConflictMulti
        : candidate.conflict === 'cname'
          ? zh.hubs.cfDnsRecordConflictCname
          : candidate.boundNodeName
            ? fmt(zh.hubs.cfDnsRecordBound, { name: candidate.boundNodeName })
            : null;
    const tags = [candidate.record.managed ? zh.hubs.cfDnsRecordManaged : null, reason]
      .filter(Boolean)
      .map((tag) => ` · ${tag}`)
      .join('');
    return {
      value: candidate.record.id,
      label: `${candidate.record.name} → ${candidate.record.content}${tags}`,
      description: reason ?? undefined,
      disabled: !candidate.selectable,
    };
  });
  const cfRecordsError = (() => {
    const error = cfRecordsQuery.error;
    if (!error) return null;
    if (error instanceof ApiError) {
      if (error.code === 'cf_dns_permission') return zh.hubs.cfDnsReadFailedPermission;
      if (error.code === 'cf_dns_auth') return zh.hubs.cfDnsReadFailedAuth;
      if (error.code === 'cf_dns_network') return zh.hubs.cfDnsReadFailedNetwork;
    }
    return fmt(zh.hubs.cfDnsReadFailedOther, { error: error.message });
  })();

  useEffect(() => {
    if (!search.create) return;
    setDrawerOpen(true);
    void navigate({ to: '/hubs', search: { create: undefined }, replace: true });
  }, [navigate, search.create]);

  const loadInstallCommand = async (
    nodeId: string,
    opts: { withCaddy: boolean; tls: boolean; tlsCn: string },
  ) => {
    setInstallLoading(true);
    setInstallError(null);
    setInstallCommand(null);
    try {
      const cn = opts.tlsCn.trim();
      if (cn && !/^[A-Za-z0-9.*-]+$/.test(cn)) {
        setInstallError(zh.hubs.installTlsCnInvalid);
        return;
      }
      const releases = await fetchAgentReleases();
      const release = releases[0];
      if (!release) {
        setInstallError(zh.hubs.installUnavailable);
        return;
      }
      const result = await generateAgentCommand(nodeId, 'install', {
        mode: 'system',
        packageUrl: release.url,
        sha256: release.sha256,
        installOptions: {
          withCaddy: opts.withCaddy,
          tls: opts.tls,
          ...(cn ? { tlsCn: cn } : {}),
        },
      });
      setInstallCommand(result.command);
      if (result.token) setToken(result.token);
    } catch (error) {
      setInstallError(error instanceof Error ? error.message : zh.hubs.installFailed);
    } finally {
      setInstallLoading(false);
    }
  };

  // P0-1：节点创建成功或安装选项变化时，实时重新生成一键安装命令。
  useEffect(() => {
    if (!createdNodeId) return;
    void loadInstallCommand(createdNodeId, installOpts);
    // installOpts 变化即重生成；loadInstallCommand 每次取最新闭包，不进依赖。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdNodeId, installOpts]);

  // 代建 DNS：失败只提示、不阻断建节点；抽屉里写明原因。
  const provisionDns = async (node: Node, selection: { zoneId: string; subdomain: string }): Promise<string | null> => {
    try {
      const result = await createCfDnsRecordForNode(node.id, selection);
      const domain = result.domain ?? result.record.name;
      setCreatedDomain(domain);
      setDnsError(null);
      toast.show({ message: fmt(zh.hubs.cfDnsCreated, { name: domain }) });
      return domain;
    } catch (error) {
      setDnsError(error instanceof Error ? error.message : zh.hubs.cfDnsCreateFailedFallback);
      return null;
    }
  };

  const createMutation = useMutation({
    mutationFn: createHub,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['hubs'] });
      const zoneId = form.zoneId;
      const subdomain = form.subdomain.trim().toLowerCase();
      const recordId = form.recordId;
      const chosen = cfRecordsQuery.data?.records.find((record) => record.id === recordId);
      setToken(data.token);
      setCreatedName(data.node.name);
      setCreatedEndpoint(data.node.endpoint);
      setCreatedNodeId(data.node.id);
      setCreatedDomain(null);
      setDnsError(null);
      setForm(EMPTY_FORM);
      setFormError(null);
      if (chosen) {
        // 只读选用：不建、不改 DNS 记录，只把域名写入节点。
        setCreatedDomain(chosen.name);
        setInstallOpts({ withCaddy: true, tls: false, tlsCn: chosen.name });
        toast.show({ message: fmt(zh.hubs.cfDnsRecordUsed, { name: chosen.name }) });
        return;
      }
      if (recordId) {
        setDnsError(zh.hubs.cfDnsRecordMissing);
        return;
      }
      if (!zoneId) return;
      const effectiveSubdomain = subdomain || data.node.name.toLowerCase().replace(/[^a-z0-9-]/g, '-');
      const domain = await provisionDns(data.node, { zoneId, subdomain: effectiveSubdomain });
      if (domain) {
        // 域名建好后安装命令自动带 Caddy + TLS，面板仍可见可改
        setInstallOpts({ withCaddy: true, tls: false, tlsCn: domain });
      }
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : zh.hubs.createFailed);
    },
  });

  const columns: ColumnDef<Node>[] = [
    {
      key: 'name',
      type: 'name',
      header: zh.hubs.colName,
      value: (node) => node.name,
      dot: (node) =>
        node.status === 'online' ? 'ok' : node.status === 'offline' ? 'danger' : 'neutral',
    },
    {
      key: 'status',
      type: 'status',
      header: zh.hubs.colStatus,
      status: (node) => ({
        tone: node.status === 'online' ? 'ok' : node.status === 'offline' ? 'danger' : 'neutral',
        // unknown = 从未探测/租约过期，不是离线证据，显示「未探测」
        label: node.status === 'unknown' ? zh.status.unprobed : zh.status[node.status],
      }),
    },
    {
      key: 'endpoint',
      type: 'mono',
      header: zh.hubs.colEndpoint,
      value: (node) => node.endpoint,
    },
    {
      key: 'lastSeen',
      type: 'time',
      header: zh.hubs.colLastSeen,
      value: (node) => node.lastSeenAt ?? undefined,
    },
    { key: 'actions', type: 'actions', header: '' },
  ];

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setToken(null);
    setCreatedNodeId(null);
    setCreatedEndpoint('');
    setCreatedDomain(null);
    setDnsError(null);
    setInstallCommand(null);
    setInstallError(null);
    setInstallOpts({ withCaddy: false, tls: false, tlsCn: '' });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setToken(null);
    setCreatedNodeId(null);
    setCreatedEndpoint('');
    setCreatedDomain(null);
    setDnsError(null);
    setInstallCommand(null);
    setInstallError(null);
    setInstallOpts({ withCaddy: false, tls: false, tlsCn: '' });
    setFormError(null);
  };

  const submitCreate = async () => {
    const name = form.name.trim();
    if (!name) {
      setFormError(zh.hubs.missingFields);
      return;
    }
    if (form.recordId && !selectedRecord) {
      setFormError(zh.hubs.cfDnsRecordMissing);
      return;
    }
    if (selectedCandidate && !selectedCandidate.selectable) {
      setFormError(zh.hubs.cfDnsRecordUnavailable);
      return;
    }
    const port = form.port.trim();
    if (selectedRecord && (!/^\d+$/.test(port) || Number(port) < 1 || Number(port) > 65535)) {
      setFormError(zh.hubs.cfDnsRecordPortInvalid);
      return;
    }
    const endpoint = selectedRecord ? endpointFromRecord(selectedRecord.content, port) : form.endpoint.trim();
    if (!endpoint) {
      setFormError(zh.hubs.missingFields);
      return;
    }
    await createMutation.mutateAsync({
      name,
      endpoint,
      tunnelEndpoint: form.tunnelEndpoint.trim() || undefined,
      domain: selectedRecord?.name,
    });
  };

  const handleAction = (ref: ActionRef, node?: Node) => {
    if (ref.id === 'hub.create') {
      openCreate();
      return;
    }
    if (ref.id === 'hub.statusRefresh') {
      void refreshStatuses();
      return;
    }
    if (ref.id === 'hub.open' && node) {
      void navigate({ href: paths.hub(node.id) });
      return;
    }
    if (ref.id === 'hub.delete' && node) {
      setDeleteWithScript(true);
      setDeleteDns(true);
      setDeleteTarget(node);
    }
  };

  /** L3 删除：先按需生成卸载脚本，再删 D1 记录（脚本自包含，删后仍可复制/下载）。 */
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const name = deleteTarget.name;
    let script: AgentCommandResult | null = null;
    if (deleteWithScript) {
      script = await generateAgentCommand(deleteTarget.id, 'uninstall', { mode: 'system' });
    }
    let dnsErrors: string[] = [];
    if (deleteDns) {
      try {
        const result = await deleteCfDnsRecordsForNode(deleteTarget.id);
        dnsErrors = result.errors ?? [];
      } catch (error) {
        dnsErrors = [error instanceof Error ? error.message : zh.nodeSettings.dnsDeleteFailed];
      }
    }
    await deleteHub(deleteTarget.id);
    await queryClient.invalidateQueries({ queryKey: ['hubs'] });
    toast.show({
      message:
        dnsErrors.length > 0
          ? fmt(zh.nodeSettings.dnsDeleteErrors, { items: dnsErrors.join('、') })
          : zh.nodeSettings.deleted,
    });
    setDeleteTarget(null);
    if (script) setUninstallScript({ name, command: script });
  };

  return (
    <Stack gap={4}>
      <PageHeader
        title={zh.hubs.title}
        summary={zh.hubs.summary}
        primaryAction={action('hub.create')}
        secondaryActions={[action('hub.statusRefresh')]}
        pendingAction={refreshing ? 'hub.statusRefresh' : undefined}
        onAction={(ref) => handleAction(ref)}
      />

      {hubsQuery.error ? (
        <Banner
          tone="danger"
          title={zh.hubs.loadError}
          description={(hubsQuery.error as Error).message}
          action={
            <Button label={zh.common.retry} size="sm" onClick={() => void hubsQuery.refetch()} />
          }
        />
      ) : null}

      {probeError ? (
        <Banner
          tone="warn"
          title={zh.hubs.statusRefreshFailedTitle}
          description={probeError}
          action={
            <Button
              label={zh.hubs.statusRefresh}
              size="sm"
              loading={refreshing}
              onClick={() => void refreshStatuses()}
            />
          }
        />
      ) : null}

      <Table
        rows={hubsQuery.data}
        columns={columns}
        rowKey={(node) => node.id}
        objectLabel={zh.nav.hubs}
        search={{
          value: searchText,
          onChange: setSearchText,
          placeholder: zh.hubs.searchPlaceholder,
        }}
        empty={{
          icon: 'server',
          title: zh.hubs.emptyTitle,
          description: zh.hubs.emptyHint,
          action: (
            <Button label={zh.actions['hub.create']} variant="primary" icon="plus" onClick={openCreate} />
          ),
        }}
        rowActions={(node) => [action('hub.open'), action('hub.delete')]}
        onRowAction={(ref, node) => handleAction(ref, node)}
        onRowOpen={(node) => void navigate({ href: paths.hub(node.id) })}
      />

      <Drawer
        open={drawerOpen}
        title={token ? zh.hubs.tokenTitle : zh.hubs.createTitle}
        context={token ? zh.hubs.tokenContext : zh.hubs.createContext}
        onClose={closeDrawer}
        footer={
          token ? (
            <Row gap={2}>
              <Button
                label={zh.common.finish}
                variant="primary"
                onClick={() => {
                  toast.show({ message: zh.toast.hubCreated });
                  closeDrawer();
                }}
              />
            </Row>
          ) : (
            <Row gap={2}>
              <Button label={zh.common.cancel} variant="default" onClick={closeDrawer} />
              <Button
                label={zh.hubs.add}
                variant="primary"
                icon="plus"
                loading={createMutation.isPending}
                loadingLabel={zh.hubs.creating}
                disabled={
                  !form.name.trim() || (form.recordId ? !selectedRecord : !form.endpoint.trim())
                }
                disabledReason={zh.hubs.missingFields}
                onClick={submitCreate}
              />
            </Row>
          )
        }
      >
        {token ? (
          <Stack gap={4}>
            <Banner tone="info" title={zh.hubs.createdTitle} description={zh.hubs.createdHint} />
            <Banner tone="warn" title={zh.hubs.tokenWarning} />
            <SecretField kind="once" text={token} />
            <KeyValue
              items={[
                { key: zh.fields.name, value: createdName },
                { key: zh.fields.endpoint, value: createdEndpoint || '—', mono: true, mode: 'middle' },
                { key: zh.hubs.cfDnsDomainField, value: createdDomain ?? '—', mono: true },
              ]}
            />
            {dnsError ? (
              <Banner tone="danger" title={zh.hubs.cfDnsCreateFailedTitle} description={dnsError} />
            ) : null}
            <Stack gap={2}>
              <p className="ui-field-label">{zh.hubs.installStepTitle}</p>
              <p className="ui-field-help">{zh.hubs.installStepHint}</p>
              <Stack gap={1}>
                <p className="ui-field-label">{zh.hubs.installOptionsTitle}</p>
                <Checkbox
                  checked={installOpts.withCaddy}
                  onChange={(checked) => setInstallOpts((prev) => ({ ...prev, withCaddy: checked }))}
                  label={zh.hubs.installWithCaddy}
                />
                <Checkbox
                  checked={installOpts.tls}
                  onChange={(checked) => setInstallOpts((prev) => ({ ...prev, tls: checked }))}
                  label={zh.hubs.installTls}
                />
                {installOpts.tls ? (
                  <Field
                    id="install-tls-cn"
                    label={zh.hubs.installTlsCn}
                    help={zh.hubs.installTlsCnHelp}
                    optional
                  >
                    <TextInput
                      id="install-tls-cn"
                      value={installOpts.tlsCn}
                      mono
                      autoComplete="off"
                      placeholder="agent.example.com"
                      onChange={(value) => setInstallOpts((prev) => ({ ...prev, tlsCn: value }))}
                    />
                  </Field>
                ) : null}
                <p className="ui-field-help">{zh.hubs.installOptionsHint}</p>
              </Stack>
              {installLoading ? <Skeleton height={80} /> : null}
              {installCommand ? (
                <CodeBlock code={installCommand} label={zh.agent.command} />
              ) : null}
              {!installLoading && !installCommand && installError ? (
                <InlineError message={installError} />
              ) : null}
              <p className="ui-field-help">{zh.hubs.installStepAfter}</p>
            </Stack>
            {createdNodeId ? (
              <Button
                label={zh.hubs.goToNode}
                size="sm"
                onClick={() => {
                  closeDrawer();
                  void navigate({ href: paths.hub(createdNodeId) });
                }}
              />
            ) : null}
          </Stack>
        ) : (
          <Stack gap={4}>
            <Field id="hub-name" label={zh.fields.name} help={zh.help.hubName}>
              <TextInput
                id="hub-name"
                value={form.name}
                autoComplete="off"
                onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
              />
            </Field>
            {selectedRecord ? (
              <Grid cols={2} gap={4}>
                <Field
                  id="hub-record-host"
                  label={zh.hubs.cfDnsRecordHostLabel}
                  help={fmt(zh.hubs.cfDnsRecordUseHint, {
                    name: selectedRecord.name,
                    type: selectedRecord.type,
                    content: selectedRecord.content,
                  })}
                >
                  <TextInput
                    id="hub-record-host"
                    value={selectedRecord.content}
                    mono
                    readOnly
                    onChange={() => {}}
                  />
                </Field>
                <Field id="hub-record-port" label={zh.hubs.cfDnsRecordPortLabel}>
                  <TextInput
                    id="hub-record-port"
                    value={form.port}
                    inputMode="numeric"
                    onChange={(value) => setForm((prev) => ({ ...prev, port: value }))}
                  />
                </Field>
              </Grid>
            ) : (
              <Field id="hub-endpoint" label={zh.fields.endpoint} help={zh.help.hubEndpoint}>
                <TextInput
                  id="hub-endpoint"
                  value={form.endpoint}
                  mono
                  placeholder="http://<IP>:8880"
                  onChange={(value) => setForm((prev) => ({ ...prev, endpoint: value }))}
                />
              </Field>
            )}
            <Field
              id="hub-tunnel"
              label={zh.fields.tunnelEndpoint}
              help={zh.help.hubTunnel}
              optional
            >
              <TextInput
                id="hub-tunnel"
                value={form.tunnelEndpoint}
                mono
                onChange={(value) => setForm((prev) => ({ ...prev, tunnelEndpoint: value }))}
              />
            </Field>
            {cfZones.length > 0 ? (
              <Stack gap={2}>
                <Field id="hub-zone" label={zh.hubs.cfDnsZoneLabel}>
                  <Select
                    id="hub-zone"
                    value={form.zoneId}
                    placeholder={zh.hubs.cfDnsZonePlaceholder}
                    options={cfZones.map((zone) => ({
                      value: zone.id,
                      label: `${zone.name} · ${zone.accountName}`,
                    }))}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, zoneId: value, recordId: '', subdomain: '' }))
                    }
                  />
                </Field>
                {form.zoneId ? (
                  <>
                    {cfRecordsQuery.isLoading ? (
                      <p className="ui-field-help">{zh.hubs.cfDnsRecordsLoading}</p>
                    ) : null}
                    {cfRecordsError ? (
                      <Banner
                        tone="warn"
                        title={zh.hubs.cfDnsRecordsFailedTitle}
                        description={`${cfRecordsError}。${zh.hubs.cfDnsRecordsFailedHint}`}
                      />
                    ) : null}
                    {!cfRecordsQuery.isLoading &&
                    !cfRecordsError &&
                    recordOptions.length > 0 ? (
                      <Field
                        id="hub-record"
                        label={zh.hubs.cfDnsRecordsLabel}
                        help={zh.hubs.cfDnsRecordSelectHelp}
                        optional
                      >
                        <Select
                          id="hub-record"
                          value={form.recordId}
                          placeholder={zh.hubs.cfDnsRecordsPlaceholder}
                          options={recordOptions}
                          onChange={(value) =>
                            setForm((prev) => {
                              const match = /:(\d+)(?:\/)?$/.exec(prev.endpoint.trim());
                              return {
                                ...prev,
                                recordId: value,
                                subdomain: '',
                                port: match?.[1] ?? prev.port,
                              };
                            })
                          }
                        />
                      </Field>
                    ) : null}
                    {!cfRecordsQuery.isLoading &&
                    !cfRecordsError &&
                    recordOptions.length === 0 ? (
                      <p className="ui-field-help">{zh.hubs.cfDnsRecordsEmpty}</p>
                    ) : null}
                    {!selectedRecord ? (
                      <>
                        <Field id="hub-subdomain" label={zh.hubs.cfDnsSubdomainLabel} optional>
                          <TextInput
                            id="hub-subdomain"
                            value={form.subdomain}
                            mono
                            autoComplete="off"
                            placeholder={zh.hubs.cfDnsSubdomainPlaceholder}
                            onChange={(value) => setForm((prev) => ({ ...prev, subdomain: value }))}
                          />
                        </Field>
                        <p className="ui-field-help">
                          {fmt(zh.hubs.cfDnsWillCreate, {
                            name: `${form.subdomain.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}.${cfZones.find((zone) => zone.id === form.zoneId)?.name ?? ''}`,
                          })}
                        </p>
                      </>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            ) : (
              <Banner tone="info" title={zh.hubs.cfDnsDisabledHint} />
            )}
            <Banner tone="info" title={zh.hubs.createNote} />
            {formError ? <InlineError message={formError} /> : null}
          </Stack>
        )}
      </Drawer>

      {deleteTarget ? (
        <ConfirmAction
          open
          action={action('hub.delete')}
          title={fmt(zh.hubs.deleteTitle, { name: deleteTarget.name })}
          body={zh.hubs.deleteBody}
          confirmLabel={zh.hubs.deleteConfirm}
          targetName={deleteTarget.name}
          nameLabel={fmt(zh.hubs.deleteNameLabel, { name: deleteTarget.name })}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        >
          <Stack gap={2}>
            <Checkbox
              checked={deleteWithScript}
              onChange={setDeleteWithScript}
              label={zh.agent.deleteWithScript}
              description={zh.agent.deleteWithScriptHint}
            />
            <Checkbox
              checked={deleteDns}
              onChange={setDeleteDns}
              label={zh.nodeSettings.dnsDeleteLabel}
              description={zh.nodeSettings.dnsDeleteHint}
            />
          </Stack>
        </ConfirmAction>
      ) : null}

      <Drawer
        open={Boolean(uninstallScript)}
        title={fmt(zh.agent.uninstallScriptTitle, { name: uninstallScript?.name ?? '' })}
        context={zh.agent.uninstallScriptContext}
        onClose={() => setUninstallScript(null)}
        footer={
          <Row gap={2} wrap>
            <Button label={zh.common.close} onClick={() => setUninstallScript(null)} />
            <Button
              label={zh.agent.downloadScript}
              icon="download"
              onClick={() => {
                if (!uninstallScript) return;
                downloadTextFile(
                  uninstallScript.command.command,
                  `khc-agent-uninstall-${uninstallScript.name}.sh`,
                );
              }}
            />
          </Row>
        }
      >
        {uninstallScript ? (
          <Stack gap={3}>
            <Banner tone="info" title={zh.agent.scriptAfterDeleteTitle} description={zh.agent.scriptAfterDeleteHint} />
            {uninstallScript.command.token ? (
              <CodeBlock code={uninstallScript.command.token} label={zh.agent.tokenLabel} />
            ) : null}
            <CodeBlock code={uninstallScript.command.command} label={zh.agent.commandUninstall} />
          </Stack>
        ) : null}
      </Drawer>
    </Stack>
  );
}
